import { ColumnSource, cssStyleToStyle } from "@omegagrid/grid";
import { getThemeManager } from "@omegagrid/core";

type GridItem = Record<string, any>;

interface PropDef {
	key: string;
	transform?: (v: any) => any;
}

interface Column {
	data?: string|number;
	[k: string]: any;
}

const CELL_TYPE_MAP: Record<string, string> = {
	numeric: 'n',
	text: 't',
	dropdown: 's',
	date: 'd',
	datetime: 'dt',
	actions: 'a',
	html: 'h',
	checkbox: 'b',
	n: 'n',
	t: 't',
	s: 's',
	m: 'm',
	d: 'd',
	dt: 'dt',
	tm: 'tm',
	a: 'a',
	h: 'h',
	b: 'b',
	g: 'g'
};

function isPlainObject(v: unknown): v is Record<string, any> {
	return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function convertComplexValue(value: any): any {
	if (typeof value !== 'string' || value === '') return value;
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

function transformHeader(headerDefs: any): any {
	if (!Array.isArray(headerDefs)) return headerDefs;
	return headerDefs.map((h: any) => {
		if (!isPlainObject(h)) return { label: h };
		const result: GridItem = { ...h };
		if (result.colspan !== undefined) result.cs = result.colspan;
		if (result.rowspan !== undefined) result.rs = result.rowspan;
		if (result.style) {
			result.style = transformStyle(result.style);
			if (result?.style?.alignment?.vertical && !result?.style?.alignment?.horizontal) {
				result.style.alignment.horizontal = 'center';
			}
		}
		return result;
	});
}

let darkMode: boolean;
function isDarkMode() {
	if (darkMode != null) return darkMode;
	darkMode = getThemeManager().activeTheme.type == 'dark';
	return darkMode;
}

function transformStyle(styleDef: any): any {
	if (styleDef === null || styleDef === undefined || styleDef === '') return null;

	if (!Array.isArray(styleDef)) {
		if (typeof styleDef === 'string') {
			const first = styleDef[0];
			if (first === '[' || first === '{') {
				try {
					styleDef = JSON.parse(styleDef);
				} catch {
					return null;
				}
			}
		} else if (typeof styleDef !== 'object') {
			return styleDef;
		}
	}

	if (!Array.isArray(styleDef)) {
		styleDef = [{ style: styleDef }];
	}

	let cssStyle = styleDef[0]?.style;

	if (!cssStyle) return null;

	const style  = cssStyleToStyle(cssStyle);
	if (style.fgColor && !style.color && isDarkMode()) {
		// original styles were always made for light mode so we asume light background if text color is not set
		style.color = '000000'; // black text for light background
	}
	
	return style;
}

function transformFormat(format: string) {
	if (format && format.charAt(0) == '0') {
		// replace all zeros before first comma with '#'
		const commaIndex = format.indexOf(',');
		if (commaIndex > 0) {
			const dotIndex = format.indexOf('.');
			format = '#,0' + (dotIndex > -1 ? format.substring(dotIndex) : '');
		}
	}
	return format;
}

function transformActionValue(action: Record<string, any>): Record<string, any> {
	if (action.action == 'delete') action.color = 'red';
	else if (action.action == 'insert') action.color = 'blue';
	return action;
}


function transformValue(value: any) : any {
	if (!Array.isArray(value)) return value;
	return value.map(v => v == null ? null : transformActionValue(v));
}

const PROP_DESCRIPTION: Record<string, PropDef> = {
	ROW: { key: 'r' },
	COL: { key: 'c' },
	PARENT: { key: 'p' },
	FORMAT: { key: 'z', transform: v => transformFormat(v) },
	TYPE: { key: 't', transform: v => CELL_TYPE_MAP[v] ?? 'g' },
	WIDTH: { key: 'w' },
	HEIGHT: { key: 'h' },
	HIDDEN: { key: 'hidden' },
	LEVEL: { key: 'l' },
	COLLAPSED: { key: 'collapsed' },
	NAME: { key: 'name', transform: v => String(v ?? '').replace(/[^0-9a-z_]/gi, '_') },
	INDENT: { key: 'indent' },
	EDITABLE: { key: 'e' },
	CLICKABLE: { key: 'clickable' },
	COLSPAN: { key: 'cs' },
	ROWSPAN: { key: 'rs' },
	VALUE: { key: 'v', transform: transformValue},
	FILTERABLE: { key: 'filterable' },
	SORTABLE: { key: 'sortable' },
	SORTING: { key: 'sorting' },
	DISPLAYVALUE: { key: 'dv' },
	FORMULA: { key: 'f' },
	DATA: { key: 'data' },
	STYLES: { key: 's', transform: v => transformStyle(v) },
	STYLE: { key: 's', transform: v => convertComplexValue(v) },
	HEADER: { key: 'header', transform: v => transformHeader(convertComplexValue(v) || []) },
	HEADERS: { key: 'header', transform: v => transformHeader(convertComplexValue(v) || []) },
	PROPS: { key: 'props', transform: v => convertComplexValue(v) },
	PROPERTIES: { key: 'props', transform: v => convertComplexValue(v) },
	FILTER: { key: 'filter' },
	DROPDOWNITEMS: { key: 'content', transform: v => convertComplexValue(v) },
	PRIORITY: { key: 'priority' },
	CONTENT: { key: 'content', transform: v => convertComplexValue(v) }
};

export class GridNormalizer {

	static normalizeDataItem(item: GridItem, columns?: (string|number)[] | null): GridItem {
		const translated: GridItem = {};
		let customProps: GridItem = {};
		let hasDropdown = false;
		for (const [k, v] of Object.entries(item)) {
			const k2 = k.toUpperCase().replace(/_/g, '');
			const prop = PROP_DESCRIPTION[k2];

			if (prop) {
				translated[prop.key] = prop.transform ? prop.transform(v) : v;
				if (prop.key === 'props' && isPlainObject(translated.props)) {
					customProps = { ...translated.props, ...customProps };
				}
			} else if (k.startsWith('PROP_')) {
				customProps[k.substring(5)] = v;
			} else if (k2 === 'DROPDOWNSELECT') {
				if (v) {
					hasDropdown = true;
					customProps['ddq'] = v;
				}
			} else if (k2 === 'DETAIL') {
				if (v) {
					translated.clickable = true;
					customProps['detail'] = v;
				}
			} else if (k2 === 'DEFAULTS') {
				customProps['__defaults'] = GridNormalizer.normalizeDataItem(convertComplexValue(v));
			} else if (columns == null) {
				translated[k] = v;
				if (k === 'props' && isPlainObject(v)) {
					customProps = { ...v, ...customProps };
				}
			}
		}

		if (columns != null) {
			translated.data = columns.map(c => c == null ? null : (item[c] ?? null));
		}

		if (hasDropdown) {
			translated.t =  's'; // force select type for cells with dropdown definition
		}

		if (Object.keys(customProps).length > 0) translated.props = customProps;

		return translated;
	}

	static normalizeRuleItem(item: GridItem): GridItem {
		const lower: GridItem = {};
		for (const [k, v] of Object.entries(item)) lower[k.toLowerCase()] = v;

		const norm: GridItem = {};
		if (lower.type !== undefined) norm.type = lower.type;
		if (lower.range !== undefined) norm.ranges = lower.range;
		if (lower.ranges !== undefined) norm.ranges = lower.ranges;
		if (lower.value !== undefined) norm.v = lower.value;
		if (lower.v !== undefined) norm.v = lower.v;
		if (lower.condition !== undefined) norm.condition = lower.condition;
		if (lower.stop !== undefined) norm.stop = Boolean(lower.stop);
		if (lower.style !== undefined) {
			if (typeof lower.style === 'string') {
				try {
					norm.style = JSON.parse(lower.style);
				} catch {
					norm.style = null;
				}
			} else {
				norm.style = lower.style;
			}
		}

		const thresh: GridItem[] = [];
		let i = 1;
		while (lower[`thresh_${i}_t`] !== undefined) {
			const t: GridItem = { t: lower[`thresh_${i}_t`] };
			if (lower[`thresh_${i}_v`] !== undefined) t.v = lower[`thresh_${i}_v`];
			if (lower[`thresh_${i}_f`] !== undefined) t.f = lower[`thresh_${i}_f`];
			thresh.push(t);
			i++;
		}
		norm.thresh = thresh;
		return norm;
	}

	static normalizeOptions(options: GridItem): GridItem {
		if (options.upload || options.saveMode) {
			const saveMode = options.saveMode || options.upload;
			options.saveMode = Array.isArray(saveMode) ? saveMode : [saveMode];
		}
		return options;
	}

	static normalizeDropdownItem(item: GridItem): GridItem {
		if ('key' in item) {
			item.k = item.key;
			delete item.key;
		}
		if ('value' in item) {
			item.v = item.value;
			delete item.value;
		}
		if ('level' in item) {
			item.l = item.level;
			delete item.level;
		}
		if ('selectable' in item) {
			item.ch = item.selectable;
			delete item.selectable;
		}
		return item;
	}

	static normalizeColumns(columns: ColumnSource[]) {
		let definedHeights: Record<number, number> = {};
		let lineCounts: Record<number, number> = {};
		
		columns.forEach(column => Array.isArray(column.header) ? column.header.forEach((h, i) => {
			if (!h) return;

			if (h.label) h.label = h.label.toString().trim();

			if (typeof h.label === 'string' && !h.html) {
				h.label = h.label.replace(/<br\s*\/?>/g, "\n").trim();
			}

			if (h.height != null) definedHeights[i] = Math.max(definedHeights[i] ?? 0, h.height);

			if (h.label) {
				lineCounts[i] = Math.max(lineCounts[i] ?? 0, h.html 
					? (h.label.match(/<br\s*\/?>/g)?.length + 1)
					: h.label.split("\n").length);
			}
		}) : null);

		if (Object.keys(lineCounts).length > 0) {
			columns.forEach(column => Array.isArray(column.header) ? column.header.forEach((h, i) => {
				if (!definedHeights[i] && lineCounts[i] > 1) {
					h.height = lineCounts[i] * 22; // Assuming 20px per line as a default height
				}
			}) : null);
		}

		return columns;
	}
}
